import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Section, SectionTitle, Highlight } from './PageElements';
import { media } from '../../styles/breakpoints';
import { parseChangelog } from '../../utils/parseChangelog';

/**
 * Reusable "What's New" section for project pages.
 *
 * Designed to live near the top of a project page to advertise the most recent
 * changes — works equally well for dev projects, music releases, design updates,
 * or anything else where a small chronological feed makes sense.
 *
 * Two ways to feed it:
 *
 *   1. Pass `entries` directly (curated, in display order):
 *
 *        <WhatsNewSection
 *            entries={[
 *                { date: 'May 2026', label: 'Native compile', description: '...', tag: 'feature' },
 *            ]}
 *        />
 *
 *   2. Pass `changelogUrl` to fetch and parse a Keep-a-Changelog file at runtime:
 *
 *        <WhatsNewSection
 *            changelogUrl="/assets/FaustStudio/CHANGELOG.md"
 *            maxEntries={20}
 *        />
 *
 * Description strings may contain <Highlight>...</Highlight> wrappers, which are
 * parsed and rendered as the project page's primary-color highlight (matching the
 * convention used elsewhere on the site).
 *
 * The entry list is capped in height and scrolls internally, so the section
 * stays compact even when the underlying changelog is long.
 */

const Wrapper = styled(Section)`
    /* Compact relative to a full Section so it can sit close to the hero. */
    padding-top: 30px;
    padding-bottom: 30px;
`;

const Intro = styled.p`
    font-size: 16px;
    color: var(--text-muted);
    line-height: 1.6;
    max-width: 800px;
    margin: 0 auto 25px;
    text-align: center;
`;

const FadeWrapper = styled.div`
    position: relative;
    max-width: 900px;
    margin: 0 auto;

    &::before,
    &::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        height: 48px;
        pointer-events: none;
        transition: opacity 180ms ease;
    }

    &::before {
        top: 0;
        background: linear-gradient(to top, rgba(0, 0, 0, 0), var(--background-color));
        opacity: ${({ $atTop }) => ($atTop ? 0 : 1)};
    }

    &::after {
        bottom: 0;
        background: linear-gradient(to bottom, rgba(0, 0, 0, 0), var(--background-color));
        opacity: ${({ $atBottom }) => ($atBottom ? 0 : 1)};
    }
`;

const ScrollContainer = styled.div`
    max-height: min(60vh, 520px);
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    padding-right: 6px;

    &::-webkit-scrollbar {
        width: 8px;
    }
    &::-webkit-scrollbar-track {
        background: rgba(255, 255, 255, 0.04);
    }
    &::-webkit-scrollbar-thumb {
        background: rgba(255, 51, 153, 0.4);
        border-radius: 4px;
    }
    &::-webkit-scrollbar-thumb:hover {
        background: rgba(255, 51, 153, 0.6);
    }
`;

const EntryList = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const Entry = styled.li`
    display: grid;
    grid-template-columns: 110px minmax(0, 1fr);
    align-items: baseline;
    gap: 14px;
    padding: 12px 18px;
    background-color: rgba(15, 15, 25, 0.5);
    border-left: 3px solid var(--primary-color);

    & > * {
        min-width: 0;
    }

    ${media.sm} {
        grid-template-columns: 1fr;
        gap: 4px;
        padding: 12px 14px;
    }
`;

const EntryDate = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: var(--primary-color);
    letter-spacing: 0.5px;
    text-transform: uppercase;
    white-space: nowrap;
`;

const EntryContent = styled.div`
    min-width: 0;
`;

const EntryLabel = styled.div`
    font-weight: 600;
    color: var(--text-color);
    overflow-wrap: anywhere;
`;

const EntryDescription = styled.div`
    color: var(--text-muted);
    line-height: 1.5;
    font-size: 15px;
    overflow-wrap: anywhere;
    margin-top: 4px;
`;

const TagBadge = styled.span`
    display: inline-block;
    margin-right: 8px;
    padding: 2px 8px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border: 1px solid;
    color: ${({ $kind }) => tagColor($kind)};
    border-color: ${({ $kind }) => tagColor($kind)};
    background-color: transparent;
    vertical-align: middle;
`;

function tagColor(kind) {
    switch (kind) {
        case 'release': return '#50fa7b';
        case 'fix':     return '#f1fa8c';
        case 'feature': return 'var(--primary-color)';
        default:        return 'var(--text-muted)';
    }
}

const parseDescription = (text) => {
    if (typeof text !== 'string') return text;
    const parts = text.split(/(<Highlight>.*?<\/Highlight>)/);
    return parts.map((part, index) => {
        if (part.startsWith('<Highlight>') && part.endsWith('</Highlight>')) {
            const content = part.replace(/<\/?Highlight>/g, '');
            return <Highlight key={index}>{content}</Highlight>;
        }
        return part;
    });
};

const WhatsNewSection = ({
    title = "What's New",
    intro,
    entries,
    changelogUrl,
    maxEntries,
}) => {
    const [fetched, setFetched] = useState(null);
    const [atTop, setAtTop] = useState(true);
    const [atBottom, setAtBottom] = useState(true);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (!changelogUrl) return undefined;
        let cancelled = false;
        fetch(changelogUrl)
            .then(r => (r.ok ? r.text() : Promise.reject(new Error(`HTTP ${r.status}`))))
            .then(md => { if (!cancelled) setFetched(parseChangelog(md)); })
            .catch(() => { if (!cancelled) setFetched([]); });
        return () => { cancelled = true; };
    }, [changelogUrl]);

    const updateScrollEdges = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        const overflowing = el.scrollHeight > el.clientHeight + 1;
        const reachedTop = el.scrollTop <= 4;
        const reachedBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 4;
        setAtTop(!overflowing || reachedTop);
        setAtBottom(!overflowing || reachedBottom);
    }, []);

    const sourceEntries = changelogUrl ? fetched : entries;
    const resolved = (sourceEntries && maxEntries)
        ? sourceEntries.slice(0, maxEntries)
        : sourceEntries;
    const resolvedLength = resolved ? resolved.length : 0;

    // Recompute the fade state after layout so the initial paint matches the
    // real overflow situation, and again when the resolved list changes size.
    useLayoutEffect(() => {
        updateScrollEdges();
        const onResize = () => updateScrollEdges();
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [updateScrollEdges, resolvedLength]);

    if (changelogUrl && fetched === null) return null;
    if (!resolved || resolved.length === 0) return null;

    return (
        <Wrapper aria-label={title}>
            <SectionTitle>{title}</SectionTitle>
            {intro && <Intro>{parseDescription(intro)}</Intro>}
            <FadeWrapper $atTop={atTop} $atBottom={atBottom}>
                <ScrollContainer ref={scrollRef} onScroll={updateScrollEdges}>
                    <EntryList>
                        {resolved.map((entry, idx) => (
                            <Entry key={`${entry.date}-${idx}`}>
                                <EntryDate>{entry.date}</EntryDate>
                                <EntryContent>
                                    <EntryLabel>
                                        {entry.tag && <TagBadge $kind={entry.tag}>{entry.tag}</TagBadge>}
                                        {entry.label}
                                    </EntryLabel>
                                    {entry.description && (
                                        <EntryDescription>{parseDescription(entry.description)}</EntryDescription>
                                    )}
                                </EntryContent>
                            </Entry>
                        ))}
                    </EntryList>
                </ScrollContainer>
            </FadeWrapper>
        </Wrapper>
    );
};

export default WhatsNewSection;
